import { useEffect, useRef, useState } from 'react';

/**
 * Owns still-photo capture: grabs the current video frame onto a canvas and
 * exposes it as an object URL.
 */
export const usePhotoCapture = (
    videoRef: React.RefObject<HTMLVideoElement | null>,
    onPhotoCapture?: (blob: Blob, url: string) => void,
) => {
    const [photoURL, setPhotoURL] = useState<string | null>(null);

    const photoURLRef = useRef<string | null>(null);
    useEffect(() => {
        photoURLRef.current = photoURL;
    }, [photoURL]);

    useEffect(() => {
        return () => {
            if (photoURLRef.current) {
                URL.revokeObjectURL(photoURLRef.current);
            }
        }
    }, []);

    const takePhoto = () => {
        if (!videoRef.current || !videoRef.current.videoWidth || !videoRef.current.videoHeight) {
            return;
        }

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        if (!context) {
            return;
        }

        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(blob => {
            if (!blob) {
                // Thrown from a detached canvas callback, so a caller could
                // never catch this — log instead of throwing into the void.
                console.error('takePhoto: canvas.toBlob returned null');
                return;
            }

            const url = URL.createObjectURL(blob);
            setPhotoURL(url);
            onPhotoCapture?.(blob, url);
        })
    }

    return { photoURL, takePhoto };
}
